import { useState, useCallback } from 'react';
import useEventAwaiting from './useEventAwaiting';
import { createUUID, UUID } from '../utils/uuid';


export default function useChatAnimationCoordinator() {
  const [stagedUserMessageId, setStagedUserMessageId] = useState<UUID | null>(null);
  const [stagedUserMessageContent, setStagedUserMessageContent] = useState<string | null>(null);
  const [stagedFlowMessageIndex, setStagedFlowMessageIndex] = useState<number | null>(null);

  const [userMessageAnimationDidFinish, handleUserMessageAnimationFinish] = useEventAwaiting();
  const [userMessageDidFinishStagingAnimation, handleUserMessageDidFinishStagingAnimation] = useEventAwaiting();
  const [flowMessageAnimationDidFinish, handleFlowMessageAnimationFinish] = useEventAwaiting();

  const withNextRender = (callback: () => void) => {
    setTimeout(callback, 0);
  }

  const withStagedUserMessage = async (content: string, callback: (nextMessageId: UUID) => Promise<void>) => {
    const nextMessageId = createUUID();

    setStagedUserMessageId(nextMessageId);
    setStagedUserMessageContent(content);

    await userMessageDidFinishStagingAnimation();

    await callback(nextMessageId);

    setStagedUserMessageId(null);
    setStagedUserMessageContent(null);
  }

  const withStagedFlowMessage = async (selectionIndex: number, callback: (nextMessageId: UUID) => Promise<void>) => {
    const nextMessageId = createUUID();

    setStagedFlowMessageIndex(selectionIndex);

    await callback(nextMessageId);

    setStagedFlowMessageIndex(null);
  }

  const waitForUserMessageAnimation = useCallback(() => userMessageAnimationDidFinish(), [userMessageAnimationDidFinish]);
  const waitForFlowMessageAnimation = useCallback(() => flowMessageAnimationDidFinish(), [flowMessageAnimationDidFinish]);

  return {
    withNextRender,

    stagedUserMessageId,
    stagedUserMessageContent,
    withStagedUserMessage,

    stagedFlowMessageIndex,
    withStagedFlowMessage,

    waitForUserMessageAnimation,
    handleUserMessageAnimationFinish,

    userMessageDidFinishStagingAnimation,
    handleUserMessageDidFinishStagingAnimation,

    waitForFlowMessageAnimation,
    handleFlowMessageAnimationFinish,
  }
}
